import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/useAuth';
import { Navigate } from 'react-router-dom';

interface App {
  slug: string;
  name: string;
  url: string;
}

interface UserRow {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  role: 'admin' | 'superviseur' | 'user' | 'aas_admin' | 'aas_user' | 'mairie_admin' | 'mairie_user' | 'ssbc_admin' | 'ssbc_user' | 'doc_admin' | 'doc_pm' | 'doc_agent';
  apps: string[];
  corps_metier: string | null;
}

const ROLES = ['user', 'superviseur', 'admin', 'aas_admin', 'aas_user', 'mairie_admin', 'mairie_user', 'ssbc_admin', 'ssbc_user', 'doc_admin', 'doc_pm', 'doc_agent'] as const;
const CORPS_METIER = ['AAS', 'CAL', 'Police Municipale', 'Chef de service', 'Direction'] as const;
const NON_CATEGORISE = 'Non catégorisé';

export default function UsersPage() {
  const { user, loading: authLoading } = useAuth();

  const [apps, setApps] = useState<App[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Formulaire de création
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [role, setRole] = useState<UserRow['role']>('user');
  const [corpsMetier, setCorpsMetier] = useState<string>('');
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  // Édition d'un utilisateur existant
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editApps, setEditApps] = useState<string[]>([]);
  const [editRole, setEditRole] = useState<UserRow['role']>('user');
  const [editCorpsMetier, setEditCorpsMetier] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Filtres et recherche sur la liste des utilisateurs
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | UserRow['role']>('all');
  const [filterApp, setFilterApp] = useState<string>('all');
  const [filterCorpsMetier, setFilterCorpsMetier] = useState<string>('all');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [appsRes, usersRes] = await Promise.all([
        fetch('/api/admin-list-apps', { credentials: 'include' }),
        fetch('/api/admin-list-users', { credentials: 'include' }),
      ]);

      if (!appsRes.ok || !usersRes.ok) {
        throw new Error('Erreur lors du chargement des données');
      }

      const appsData = await appsRes.json();
      const usersData = await usersRes.json();
      setApps(appsData.apps || []);
      setUsers(usersData.users || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const isManager = user?.role === 'superviseur';

  useEffect(() => {
    if (isManager) {
      loadData();
    }
  }, [isManager]);

  if (authLoading) return null;
  if (!user || !isManager) {
    return <Navigate to="/" replace />;
  }

  const toggleApp = (slug: string, list: string[], setter: (v: string[]) => void) => {
    if (list.includes(slug)) {
      setter(list.filter((a) => a !== slug));
    } else {
      setter([...list, slug]);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccessMsg('');

    try {
      const res = await fetch('/api/admin-create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, nom, prenom, role, apps: selectedApps, corpsMetier: corpsMetier || null }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(`${data.error || 'Erreur lors de la création'}${data.details ? ' : ' + data.details : ''}`);
      }

      setSuccessMsg(`Utilisateur ${prenom} ${nom} créé avec succès !`);
      setEmail('');
      setPassword('');
      setNom('');
      setPrenom('');
      setRole('user');
      setCorpsMetier('');
      setSelectedApps([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u: UserRow) => {
    setEditingId(u.id);
    setEditApps(u.apps);
    setEditRole(u.role);
    setEditCorpsMetier(u.corps_metier || '');
    setSuccessMsg('');
    setError('');
  };

  const normalize = (str: string) =>
    str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const filteredUsers = users.filter((u) => {
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const matchesApp = filterApp === 'all' || u.apps.includes(filterApp);
    const matchesCorps =
      filterCorpsMetier === 'all' ||
      (filterCorpsMetier === NON_CATEGORISE ? !u.corps_metier : u.corps_metier === filterCorpsMetier);

    if (!searchQuery.trim()) return matchesRole && matchesApp && matchesCorps;

    const q = normalize(searchQuery);
    const terms = q.split(/\s+/).filter(Boolean);
    const haystack = `${normalize(u.prenom)} ${normalize(u.nom)} ${normalize(u.email)} ${normalize(u.role)} ${normalize(u.corps_metier || '')}`;
    const matchesSearch = terms.every((term) => haystack.includes(term));

    return matchesSearch && matchesRole && matchesApp && matchesCorps;
  });

  // Regroupement par corps de métier pour un affichage plus lisible
  const groupedUsers = (() => {
    const groups: Record<string, UserRow[]> = {};
    for (const cm of CORPS_METIER) groups[cm] = [];
    groups[NON_CATEGORISE] = [];
    for (const u of filteredUsers) {
      const key = u.corps_metier && (CORPS_METIER as readonly string[]).includes(u.corps_metier) ? u.corps_metier : NON_CATEGORISE;
      groups[key].push(u);
    }
    return [...CORPS_METIER, NON_CATEGORISE]
      .map((key) => ({ key, list: groups[key] }))
      .filter((g) => g.list.length > 0);
  })();

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setError('');

    try {
      const res = await fetch('/api/admin-update-user', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: editingId, role: editRole, apps: editApps, corpsMetier: editCorpsMetier || null }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Erreur lors de la mise à jour');
      }

      setSuccessMsg('Accès mis à jour avec succès !');
      setEditingId(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId: string, prenom: string, nom: string) => {
    if (!window.confirm(`Supprimer définitivement l'utilisateur ${prenom} ${nom} ?`)) return;

    setDeletingId(userId);
    setError('');
    setSuccessMsg('');

    try {
      const res = await fetch('/api/admin-delete-user', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Erreur lors de la suppression');
      }

      setSuccessMsg(`Utilisateur ${prenom} ${nom} supprimé avec succès.`);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-hub-bg text-hub-text px-4 py-8 pt-24">
      <div className="max-w-5xl mx-auto space-y-8">
        <h1 className="text-2xl font-bold uppercase tracking-widest">Gestion des utilisateurs</h1>

        {error && (
          <div className="bg-red-600/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="bg-green-600/20 border border-green-500/50 text-green-200 px-4 py-3 rounded-lg text-sm">
            {successMsg}
          </div>
        )}

        {/* Formulaire de création */}
        <div className="bg-hub-card border border-hub-border rounded-xl p-6">
          <h2 className="text-lg font-bold mb-4">Créer un nouvel utilisateur</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1 text-hub-muted">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text"
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-hub-muted">Mot de passe</label>
                <input
                  type="text"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text"
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-hub-muted">Nom</label>
                <input
                  type="text"
                  required
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text"
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-hub-muted">Prénom</label>
                <input
                  type="text"
                  required
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text"
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-hub-muted">Rôle</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as typeof role)}
                  className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1 text-hub-muted">Corps de métier</label>
                <select
                  value={corpsMetier}
                  onChange={(e) => setCorpsMetier(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text"
                >
                  <option value="">{NON_CATEGORISE}</option>
                  {CORPS_METIER.map((cm) => (
                    <option key={cm} value={cm}>{cm}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm mb-2 text-hub-muted">Applications (hub inclus automatiquement)</label>
              <div className="flex flex-wrap gap-2">
                {apps.filter((a) => a.slug !== 'hub' && a.slug !== 'maincourante').map((app) => (
                  <label
                    key={app.slug}
                    className={`px-3 py-1.5 rounded border cursor-pointer text-xs font-medium transition-colors ${
                      selectedApps.includes(app.slug)
                        ? 'bg-hub-blue border-hub-blue text-white'
                        : 'bg-hub-bg border-hub-border text-hub-muted'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={selectedApps.includes(app.slug)}
                      onChange={() => toggleApp(app.slug, selectedApps, setSelectedApps)}
                    />
                    {app.name}
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={creating}
              className="bg-hub-blue hover:bg-hub-blue-hover text-white font-bold text-xs uppercase tracking-widest px-6 py-2.5 rounded disabled:opacity-50"
            >
              {creating ? 'Création...' : 'Créer l\'utilisateur'}
            </button>
          </form>
        </div>

        {/* Liste des utilisateurs */}
        <div className="bg-hub-card border border-hub-border rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <h2 className="text-lg font-bold">Utilisateurs existants</h2>
            <span className="text-xs text-hub-muted">{filteredUsers.length} / {users.length} utilisateur(s)</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block text-xs mb-1 text-hub-muted">Recherche intelligente</label>
              <input
                type="text"
                placeholder="Nom, prénom, email, rôle..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text text-sm"
              />
            </div>
            <div>
              <label className="block text-xs mb-1 text-hub-muted">Filtrer par rôle</label>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value as typeof filterRole)}
                className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text text-sm"
              >
                <option value="all">Tous les rôles</option>
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1 text-hub-muted">Filtrer par application</label>
              <select
                value={filterApp}
                onChange={(e) => setFilterApp(e.target.value)}
                className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text text-sm"
              >
                <option value="all">Toutes les applications</option>
                {apps.filter((a) => a.slug !== 'hub' && a.slug !== 'maincourante').map((app) => (
                  <option key={app.slug} value={app.slug}>{app.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1 text-hub-muted">Filtrer par corps de métier</label>
              <select
                value={filterCorpsMetier}
                onChange={(e) => setFilterCorpsMetier(e.target.value)}
                className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text text-sm"
              >
                <option value="all">Tous les corps de métier</option>
                {CORPS_METIER.map((cm) => (
                  <option key={cm} value={cm}>{cm}</option>
                ))}
                <option value={NON_CATEGORISE}>{NON_CATEGORISE}</option>
              </select>
            </div>
          </div>

          {(searchQuery || filterRole !== 'all' || filterApp !== 'all' || filterCorpsMetier !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterRole('all');
                setFilterApp('all');
                setFilterCorpsMetier('all');
              }}
              className="text-xs text-hub-accent hover:opacity-80 mb-4 underline"
            >
              Réinitialiser les filtres
            </button>
          )}

          {loading ? (
            <p className="text-hub-muted text-sm">Chargement...</p>
          ) : filteredUsers.length === 0 ? (
            <p className="text-hub-muted text-sm">Aucun utilisateur ne correspond à votre recherche.</p>
          ) : (
            <div className="space-y-6">
              {groupedUsers.map(({ key, list }) => (
                <div key={key}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-hub-accent">{key}</h3>
                    <span className="text-[11px] text-hub-muted">({list.length})</span>
                    <div className="flex-1 h-px bg-hub-border" />
                  </div>
                  <div className="space-y-3">
                    {list.map((u) => (
                <div key={u.id} className="border border-hub-border rounded-lg p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold">{u.prenom} {u.nom}</p>
                      <p className="text-xs text-hub-muted">{u.email}</p>
                    </div>

                    {editingId === u.id ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={editRole}
                          onChange={(e) => setEditRole(e.target.value as typeof editRole)}
                          className="px-2 py-1 rounded bg-hub-bg border border-hub-border text-hub-text text-xs"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                        <select
                          value={editCorpsMetier}
                          onChange={(e) => setEditCorpsMetier(e.target.value)}
                          className="px-2 py-1 rounded bg-hub-bg border border-hub-border text-hub-text text-xs"
                        >
                          <option value="">{NON_CATEGORISE}</option>
                          {CORPS_METIER.map((cm) => (
                            <option key={cm} value={cm}>{cm}</option>
                          ))}
                        </select>
                        <button
                          onClick={saveEdit}
                          disabled={saving}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-3 py-1.5 rounded disabled:opacity-50"
                        >
                          {saving ? '...' : 'Enregistrer'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="bg-gray-600 hover:bg-gray-700 text-white text-xs font-bold px-3 py-1.5 rounded"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-hub-accent font-bold uppercase">{u.role}</span>
                        <button
                          onClick={() => startEdit(u)}
                          className="bg-hub-blue hover:bg-hub-blue-hover text-white text-xs font-bold px-3 py-1.5 rounded"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(u.id, u.prenom, u.nom)}
                          disabled={deletingId === u.id}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded disabled:opacity-50"
                        >
                          {deletingId === u.id ? '...' : 'Supprimer'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {editingId === u.id
                      ? apps.filter((a) => a.slug !== 'hub' && a.slug !== 'maincourante').map((app) => (
                          <label
                            key={app.slug}
                            className={`px-2.5 py-1 rounded border cursor-pointer text-[11px] font-medium transition-colors ${
                              editApps.includes(app.slug)
                                ? 'bg-hub-blue border-hub-blue text-white'
                                : 'bg-hub-bg border-hub-border text-hub-muted'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={editApps.includes(app.slug)}
                              onChange={() => toggleApp(app.slug, editApps, setEditApps)}
                            />
                            {app.name}
                          </label>
                        ))
                      : u.apps.filter((a) => a !== 'hub' && a !== 'maincourante').map((slug) => {
                          const app = apps.find((a) => a.slug === slug);
                          return (
                            <span
                              key={slug}
                              className="px-2.5 py-1 rounded bg-hub-bg border border-hub-border text-[11px] text-hub-muted"
                            >
                              {app?.name || slug}
                            </span>
                          );
                        })}
                  </div>
                </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
